/** What the Channel Settings snackbar is currently saying, if anything. */
export type ChannelSaveSnackbarMessage = 'unsaved' | 'saving';

export interface ChannelSaveSnackbarState {
  open: boolean;
  message: ChannelSaveSnackbarMessage;
}

/**
 * PSTUDIO-4253 — whether the Channel Settings snackbar is open, and which of its
 * two things it is saying.
 *
 * The snackbar is the page's ONLY save affordance: it carries both the Save
 * Changes button and, through that button's `loading`, the only indication that
 * anything is happening. `submitChannel` dismisses it as its first act — which
 * used to take the spinner off screen at the very moment the work began.
 *
 * That matters because the save does not go out immediately. Channel mutations
 * are handled only in the machine's `idle` state, so the submit path parks in
 * `awaitMachineIdle` (30s cap) behind whatever the page is still loading, and
 * the reporter's "Save changes gives no visible feedback" is precisely that
 * window: a dismissed snackbar, an untouched URL, and nothing on screen.
 *
 * So `saving` outranks both other inputs — `dismissed`, which the submit path
 * sets itself, and `touched`, so no ordering between the form reset and the
 * save settling can blink the spinner out.
 *
 * KNOWN GAP: the caller passes `isSubmitting`, which covers the work before the
 * event goes out — validation, the deferred uploads, and the wait for `idle`
 * that this ticket is about — and is cleared one microtask after the dispatch,
 * while the mutation itself is still in flight. Covering that tail as well was
 * tried and reverted: it keeps this snackbar, and the Save button inside it,
 * on screen until the save settles, which the `createChannel` e2e reads as the
 * save never finishing. The remaining blind spot is one request that ends in a
 * toast and a navigation, not the multi-second queue wait people reported.
 *
 * Pure so it can be tested — `channel-settings-form.tsx` pulls in design-system
 * Lit components and cannot be rendered in this package's test environment.
 */
export const channelSaveSnackbarState = (state: {
  touched: boolean;
  dismissed: boolean;
  saving: boolean;
}): ChannelSaveSnackbarState => {
  if (state.saving) {
    return { open: true, message: 'saving' };
  }

  return {
    open: state.touched && !state.dismissed,
    message: 'unsaved',
  };
};
